<script setup lang="ts">
/**
 * The glyph of a dynamic block grip, drawn at panel size.
 *
 * Same silhouettes and same colours as the canvas grips — built from the very
 * geometry the overlay uses, so a row in the properties panel and its grip on the
 * drawing are visibly the same object.
 */
import { computed } from "vue";
import { buildHandleShapePoints } from "@/drawing/transform/handleShapes";
import type { DynBlockRowGlyph } from "@/drawing/blocks/dynamicGrouping";

const props = withDefaults(
  defineProps<{ glyph: DynBlockRowGlyph; size?: number }>(),
  {
    size: 14,
  },
);

/** Mirrors the fillColor of the matching handle (DynPoint/DynFlip/DynList/DynAngle). */
const FILLS: Record<NonNullable<DynBlockRowGlyph>, string> = {
  square: "rgb(70, 130, 220)",
  doubleArrow: "rgb(70, 130, 220)",
  angleSector: "rgb(70, 130, 220)",
  triangleDown: "rgb(40, 100, 190)",
};

// Leave a pixel of margin so the white outline is not clipped by the viewBox.
const radius = computed(() => props.size / 2 - 1);

const points = computed(() => {
  if (!props.glyph) return "";
  const center = { x: props.size / 2, y: props.size / 2 };
  return (buildHandleShapePoints(props.glyph, center, radius.value) ?? [])
    .map((point) => `${point.x},${point.y}`)
    .join(" ");
});
</script>

<template>
  <svg
    v-if="glyph"
    class="row-glyph"
    :width="size"
    :height="size"
    :viewBox="`0 0 ${size} ${size}`"
    aria-hidden="true"
  >
    <polygon
      :points="points"
      :fill="FILLS[glyph]"
      stroke="rgba(255, 255, 255, 0.9)"
      stroke-width="1"
      stroke-linejoin="round"
    />
  </svg>
  <!-- Panel-only parameter: keep the same indent as rows that have a grip. -->
  <span v-else class="row-glyph" :style="{ width: `${size}px` }"></span>
</template>

<style lang="scss" scoped>
.row-glyph {
  flex: 0 0 auto;
  display: inline-block;
}
</style>
